/**
 * A read has THREE outcomes, not two — and the third one is what keeps lying.
 *
 * `readShape.js` (CC-50321 and family) fixed *"an empty result and a failed read
 * must never look the same"*. This is the sibling gap, found 2026-08-21 while
 * chasing an intermittent "All Reports 0 / No reports match your search" on the
 * deployed Report Center: a read that **never happened at all** also looked
 * identical to an empty one.
 *
 * The mechanism is a react-query detail with real teeth. `isLoading` is defined as
 * `isPending && isFetching`, so it is **false** for a query that is pending but not
 * in flight. Two ways that happens:
 *
 *   - `fetchStatus === 'paused'` — with the default `networkMode: 'online'`,
 *     react-query refuses to fire while `onlineManager` believes the browser is
 *     offline, and keeps refusing until it flips back. Nothing is thrown, nothing
 *     is logged, `queryFn` is **never called**, and `data` stays `undefined`.
 *   - `fetchStatus === 'idle'` with `enabled: false` — the query is deliberately
 *     switched off.
 *
 * A page that renders its empty state on `!isLoading && !error` therefore states,
 * confidently and indefinitely, that the firm has no reports / no invoices / no
 * unapplied payments, when the truth is that it never asked. Measured: the paused
 * catalog query rendered "No reports match your search" with a count of **0**, no
 * spinner and no error, while `queryFn` had been called zero times.
 *
 * In a billing application that is the same defect as a wrong number.
 *
 * Usage — replace `isLoading` / `!isLoading` at the render sites:
 *
 *     const q = useQuery({ queryKey: [...], queryFn: ... })
 *     const state = readState(q)
 *     if (state === 'failed')  → the error panel, with a retry
 *     if (state === 'blocked') → "we could not reach the server", with a retry
 *     if (state !== 'ready')   → a loading state; NEVER an empty state
 *     if (state === 'ready')   → the data, and only now may `length === 0` be
 *                                reported as genuinely empty
 *
 * Deliberately not a hook: it is a pure function of a query result, so it works
 * inside a `useMemo`, in a test, or on a `getQueryState()` snapshot.
 */

/**
 * @param {{ error?: unknown, isPending?: boolean, fetchStatus?: string, data?: unknown }} query
 *   A `useQuery` result (or anything with the same four fields).
 * @returns {'failed'|'blocked'|'idle'|'loading'|'ready'}
 */
export const readState = (query) => {
  if (!query) return 'loading'
  // Error first: a query can hold a previous `data` alongside a fresh error, and a
  // stale success must not mask a live failure.
  if (query.error) return 'failed'
  if (query.isPending) {
    // Pending but NOT in flight — the two states `isLoading` hides.
    if (query.fetchStatus === 'paused') return 'blocked'
    if (query.fetchStatus === 'idle') return 'idle'
    return 'loading'
  }
  return 'ready'
}

/**
 * True only when the read actually completed and the data may be trusted —
 * including trusted to be EMPTY. This is the predicate that belongs in front of
 * every empty state and every count.
 */
export const isReadReady = (query) => readState(query) === 'ready'

/**
 * True when the read has not completed for any reason. Use to gate a count: a `0`
 * rendered next to an unfinished read is a claim, not an observation.
 */
export const isReadUnresolved = (query) => readState(query) !== 'ready'
